import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { Container, Table, Badge, Input, Row, Col, Card, CardBody } from 'reactstrap';

// Shadow dispatcher: what the automatic dispatcher would have done for each order, why, and how that compares
// with the dispatchers' choice and what actually happened. Read-only; refreshes every 30 seconds.

const TZ = 'America/Barbados';
const REFRESH_MS = 30000;

const t = iso => (iso ? moment(iso).tz(TZ).format('h:mma') : '');
const mins = m => (m === null || m === undefined ? '' : `${Math.round(m)} min`);
// Food is ready pressed on the restaurant's tablet (shadow v1.5)
const pressed = o => !!(o.food_ready && o.food_ready.signalled_at);

class ShadowDispatch extends Component {
    state = { day: moment().tz(TZ).format('YYYY-MM-DD'), orders: [], open: {}, loading: true, error: null, filter: '', model: [], showModel: false };

    componentDidMount() {
        document.title = 'Shadow dispatcher – Mission Control';
        this.load();
        this.timer = setInterval(this.load, REFRESH_MS);
        axios.get('/api/shadow/model').then(res => this.setState({ model: res.data.runs || [] })).catch(() => {});
    }

    componentWillUnmount() { clearInterval(this.timer); }

    load = () => {
        axios.get(`/api/shadow?date=${this.state.day}`)
            .then(res => this.setState({ orders: res.data.orders, loading: false, error: null }))
            .catch(err => this.setState({ loading: false, error: (err.response && err.response.data && err.response.data.message) || err.message }));
    };

    setDay = e => this.setState({ day: e.target.value, loading: true, open: {} }, this.load);

    toggle = id => this.setState(s => ({ open: { ...s.open, [id]: !s.open[id] } }));

    // The shadow's choice: frozen at the moment dispatchers assigned the order, or its current recommendation.
    shadow(o) {
        const d = o.decision;
        if (d && d.live) return { driver: d.live.name, est: d.live, frozen: true };
        if (o.live && o.live.choice) return { driver: o.live.choice.name, est: o.live.choice, frozen: false, headline: o.live.headline };
        if (o.live) return { driver: null, frozen: false, headline: o.live.headline };
        return null;
    }

    summary(orders) {
        const decided = orders.filter(o => o.decision && o.decision.live && o.decision.dispatcher);
        const gains = decided.map(o => o.decision.model_gain_min).filter(g => g !== null && g !== undefined);
        const same = decided.filter(o => o.decision.same_driver).length;
        const delivered = decided.filter(o => o.actual && o.actual.mins_from_order && o.decision.live.mins_from_order);
        const avg = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
        return {
            orders: orders.length,
            decided: decided.length,
            same: decided.length ? same / decided.length : null,
            gain: avg(gains),
            actual: avg(delivered.map(o => o.actual.mins_from_order)),
            shadowEst: avg(delivered.map(o => o.decision.live.mins_from_order)),
            dispEst: avg(delivered.filter(o => o.decision.dispatcher.mins_from_order).map(o => o.decision.dispatcher.mins_from_order)),
            nDelivered: delivered.length,
            pressed: orders.filter(pressed).length,
            live: orders.filter(o => o.status !== 'cancelled' && o.status !== 'pre-order').length
        };
    }

    statusBadge(o) {
        const colors = { 'pre-order': 'secondary', 'waiting for a driver': 'danger', 'with a driver': 'warning', 'collected': 'primary', 'delivered': 'success', 'cancelled': 'secondary' };
        return <Badge color={colors[o.status] || 'secondary'}>{o.status || ''}</Badge>;
    }

    compareBadge(o) {
        const d = o.decision;
        if (!d || !d.dispatcher) return null;
        if (d.same_driver) return <Badge color="success">same driver</Badge>;
        const g = d.model_gain_min;
        if (g === null || g === undefined) return <Badge color="secondary">not comparable</Badge>;
        if (g > 1) return <Badge color="info" title="Model's estimate of minutes saved: this customer plus other orders">shadow ~{Math.round(g)} min better</Badge>;
        if (g < -1) return <Badge color="warning">dispatchers ~{Math.round(-g)} min better</Badge>;
        return <Badge color="secondary">about the same</Badge>;
    }

    reassignBadge(o) {
        const r = o.reassign;
        if (!r) return null;
        if (r.active) return <div><Badge color="warning" title="Moving this order now looks worth it. Click the row for why.">consider moving → {r.to_driver} (~{Math.round(r.saving_min)} min)</Badge></div>;
        return <div className="small text-muted" title="The shadow flagged this order for moving earlier; the flag has since cleared">flagged {t(r.first_at)}: move to {r.to_driver} (~{Math.round(r.max_saving_min)} min)</div>;
    }

    details(o) {
        const d = o.decision;
        const reasons = d && d.live_reasons ? d.live_reasons : (o.live ? o.live.reasons : []);
        return (
            <tr key={o._id + '-d'}>
                <td colSpan={9} style={{ background: '#fafafa', fontSize: '0.9em' }}>
                    <Row>
                        <Col md={6}>
                            <strong>{d && d.live ? `Shadow's choice when dispatchers assigned it (${t(d.at)})` : `Shadow's current recommendation (updated ${t(o.live && o.live.updated)})`}</strong>
                            {(d && d.live_headline) || (o.live && o.live.headline) ? <div>{(d && d.live_headline) || o.live.headline}</div> : null}
                            <ul className="mb-2">{(reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
                            {o.changes && o.changes.length > 1 ?
                                <div className="text-muted">Recommendation changed while waiting: {o.changes.map(c => `${t(c.at)} ${c.driver}`).join(' → ')}</div> : null}
                        </Col>
                        <Col md={6}>
                            {o.reassign ? <div className="mb-2"><strong>{o.reassign.active ? 'Consider moving this order' : `Flagged for moving (${t(o.reassign.first_at)}–${t(o.reassign.ended_at || o.reassign.last_at)})`}</strong>
                                <ul className="mb-1">{(o.reassign.reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul></div> : null}
                            <strong>Compared with the dispatchers' choice</strong>
                            {d && d.comparison ? <ul className="mb-2">{d.comparison.map((r, i) => <li key={i}>{r}</li>)}</ul>
                                : <div className="text-muted mb-2">{d && d.note ? d.note : 'Not assigned yet.'}</div>}
                            {o.actual ? <div><strong>What happened:</strong> {o.actual.driver} assigned {t(o.actual.assigned_at)}
                                {o.actual.picked_at ? `, collected ${t(o.actual.picked_at)}` : ''}
                                {o.actual.delivered_at ? `, delivered ${t(o.actual.delivered_at)} (${mins(o.actual.mins_from_order)} after the order was placed)` : ''}.</div> : null}
                            {pressed(o) ? <div><strong>Food ready {t(o.food_ready.signalled_at)}</strong> ({o.restaurant} pressed Food is ready on its tablet
                                {o.food_ready.signal_tablet_time && t(o.food_ready.signal_tablet_time) !== t(o.food_ready.signalled_at) ? `; tablet clock said ${t(o.food_ready.signal_tablet_time)}` : ''}).</div> : null}
                            {o.food_ready && o.food_ready.expected ? <div className="text-muted">Food-ready estimate: {t(o.food_ready.expected)} (likely {t(o.food_ready.likely_from)}–{t(o.food_ready.likely_to)})
                                {o.food_ready.stated_prep_min ? `; restaurant said ${Math.round(o.food_ready.stated_prep_min)} min` : ''}.</div> : null}
                        </Col>
                    </Row>
                </td>
            </tr>
        );
    }

    // The weekly retrain: when the shadow's numbers were last checked, and what changed.
    modelLine() {
        const { model, showModel } = this.state;
        if (!model.length) return null;
        const r = model[0];
        const live = model.find(m => m.version && !m.dry_run);
        return (
            <div className="mb-3 small">
                <span className="text-muted">Weekly learning: last run {moment(r.at).tz(TZ).format('ddd D MMM h:mma')} – {r.result}.
                    {live ? ` Numbers in use since ${moment(live.at).tz(TZ).format('D MMM')}.` : ' Still on the original numbers.'} </span>
                <a href="#model" onClick={e => { e.preventDefault(); this.setState({ showModel: !showModel }); }}>{showModel ? 'hide' : 'what it found'}</a>
                {showModel ? <ul className="mt-2 mb-0">{(r.summary || []).map((line, i) => <li key={i}>{line}</li>)}</ul> : null}
            </div>
        );
    }

    render() {
        const { orders, open, loading, error, day, filter } = this.state;
        const f = filter.trim().toLowerCase();
        const shown = orders.filter(o => !f || `${o.order_id} ${o.restaurant} ${o.customer}`.toLowerCase().includes(f));
        const s = this.summary(orders);
        return (
            <Container fluid className="p-3">
                <Row className="align-items-center mb-3">
                    <Col md={6}><h3 className="mb-0">Shadow dispatcher</h3>
                        <div className="text-muted">What the automatic dispatcher would have done for each order, and why. It only watches: nothing is sent to drivers.</div></Col>
                    <Col md={3}><Input type="date" value={day} onChange={this.setDay} /></Col>
                    <Col md={3}><Input type="search" placeholder="Search order, restaurant, customer" value={filter} onChange={e => this.setState({ filter: e.target.value })} /></Col>
                </Row>
                <Card className="mb-3"><CardBody className="py-2">
                    <Row>
                        <Col>Orders: <strong>{s.orders}</strong></Col>
                        <Col>Compared with dispatchers: <strong>{s.decided}</strong></Col>
                        <Col>Same driver: <strong>{s.same === null ? '–' : `${Math.round(s.same * 100)}%`}</strong></Col>
                        <Col title="The model's own estimate, including knock-on delays to other orders">Model's average gain per order: <strong>{s.gain === null ? '–' : `${s.gain.toFixed(1)} min`}</strong></Col>
                        <Col title="Orders where the restaurant pressed Food is ready on its tablet (new tablet app only)">Food ready pressed: <strong>{s.pressed} of {s.live}</strong></Col>
                        <Col title={`${s.nDelivered} delivered orders`}>Average delivery, actual: <strong>{s.actual ? mins(s.actual) : '–'}</strong> · shadow estimate: <strong>{s.shadowEst ? mins(s.shadowEst) : '–'}</strong></Col>
                    </Row>
                </CardBody></Card>
                {this.modelLine()}
                {error ? <div className="alert alert-danger">Couldn't load: {error}</div> : null}
                {loading ? <div>Loading…</div> :
                <Table size="sm" hover responsive>
                    <thead><tr>
                        <th>Ordered</th><th>Restaurant</th><th>Customer</th><th title="Delivery estimate shown to the customer">Told customer</th>
                        <th>Shadow's driver</th><th title="Estimated delivery if the shadow's choice had been followed">Shadow est. delivery</th>
                        <th>Dispatchers' driver</th><th>Actual delivery</th><th></th>
                    </tr></thead>
                    <tbody>
                        {shown.map(o => {
                            const sh = this.shadow(o);
                            const se = sh && sh.est;
                            const told = o.shown_estimate_min && o.shown_estimate_min[0] ?
                                `${o.shown_estimate_min[0]}–${o.shown_estimate_min[1]} min (${t(moment(o.order_date).add(o.shown_estimate_min[0], 'minutes'))}–${t(moment(o.order_date).add(o.shown_estimate_min[1], 'minutes'))})` : '';
                            return [
                                <tr key={o._id} onClick={() => this.toggle(o._id)} style={{ cursor: 'pointer' }}>
                                    <td>{moment(o.order_date).tz(TZ).format('D MMM h:mma')}<div className="text-muted small">#{o.order_id}</div></td>
                                    <td>{o.restaurant}{pressed(o) ? <div><Badge color="success" title="The restaurant pressed Food is ready on its tablet">food ready {t(o.food_ready.signalled_at)}</Badge></div> : null}</td>
                                    <td>{o.customer}</td>
                                    <td>{told}</td>
                                    <td>{sh ? (sh.driver || <span className="text-muted">{sh.headline}</span>) : ''}
                                        {sh && !sh.frozen && sh.driver ? <div className="small text-danger" title="The shadow's current pick while the order waits for a driver; it re-plans every minute until dispatchers assign the order">current pick · {se && se.send_at && moment(se.send_at).isAfter(moment().add(1, 'minute')) ? `send at ${t(se.send_at)}` : 'send now'}</div> : null}</td>
                                    <td>{se && se.deliver_at ? <span>{t(se.deliver_at)} <span className="text-muted">({mins(se.mins_from_order)})</span></span> : ''}</td>
                                    <td>{o.actual ? o.actual.driver : ''}<div>{this.compareBadge(o)}</div>{this.reassignBadge(o)}</td>
                                    <td>{o.actual && o.actual.delivered_at ? <span>{t(o.actual.delivered_at)} <span className="text-muted">({mins(o.actual.mins_from_order)})</span></span> : this.statusBadge(o)}</td>
                                    <td className="text-muted">{open[o._id] ? '▲' : '▼'}</td>
                                </tr>,
                                open[o._id] ? this.details(o) : null
                            ];
                        })}
                    </tbody>
                </Table>}
                {!loading && !orders.length ? <div className="text-muted">No orders for this day yet.</div> : null}
            </Container>
        );
    }
}

export default ShadowDispatch;
